/**
 * Pure URL contract for "focused dashboard" deep links (Issue #248, extended
 * to college football by Issue #539).
 *
 * A Position Tracker graph chip encodes the exact market it already knows
 * (sport, date, event identity, ticker, venue, side) into a Value Dashboard
 * URL. The Value Dashboard later re-derives the same target from live rows and
 * opens the existing trade panel through it — never a second order-entry path.
 *
 * The market KIND is deliberately absent from the query. It is derivable from
 * `ticker` alone, and the repo's established deep-link convention — Game
 * Detail's `?ticker=&side=`, which already serves moneyline, spread, total and
 * grouped prop rungs — never names it either. Adding a `kind` key would be a
 * second spelling of something the ticker already says, and two spellings are
 * how a link and its destination drift apart.
 *
 * This module is intentionally side-effect free: no React, no Zustand, no
 * view code. See `docs/position-tracker-focused-dashboard-deep-links.md` and
 * the Issue #248 plan context for the full contract this implements.
 */

import { dashboardSportMatches } from './dashboardSport';

/** How long to wait, after the feed first reports connected, before an
 *  unresolved focus query is declared unavailable. */
export const FOCUSED_DASHBOARD_WAIT_MS = 10_000;

/** Sports whose Position Tracker graph chips route to the focused dashboard.
 *  Widening this set is the ONLY sport-facing change a new sport needs — every
 *  other sport keeps its existing Game Detail destination, byte for byte.
 *  'atp-challenger' (#604) is the dashboard-only circuit key: it resolves to
 *  ordinary atp rows and never names a new authority sport. */
export type FocusedSport = 'atp' | 'atp-challenger' | 'wta' | 'cfb';
export type FocusedVenue = 'kalshi' | 'polymarket';
export type FocusedSide = 'yes' | 'no';

export interface FocusedDashboardTarget {
  sport: FocusedSport;
  date: string;
  eventIdentity: string;
  ticker: string;
  venue?: FocusedVenue;
  side: FocusedSide;
}

export type FocusUnavailableReason =
  | 'missing-event-identity'
  | 'invalid-source-identity'
  | 'ambiguous-source-identity';

export type FocusedDashboardLinkInput =
  | { kind: 'market'; target: FocusedDashboardTarget }
  | {
      kind: 'unavailable';
      sport?: FocusedSport;
      date?: string;
      ticker?: string;
      venue?: FocusedVenue;
      side?: FocusedSide;
      reason: FocusUnavailableReason;
    };

export type ParsedDashboardFocus =
  | { kind: 'none' }
  | { kind: 'market'; target: FocusedDashboardTarget; identity: string }
  | { kind: 'unavailable'; identity: string }
  | { kind: 'invalid'; identity: string };

export interface FocusableBook {
  marketTicker: string;
  tokenId?: string;
  noTokenId?: string;
}

export interface FocusableGameRow {
  key: string;
  sport: string;
  date: string;
  awayCode: string;
  homeCode: string;
  gameState: 'live' | 'pre' | 'final';
  archived?: unknown;
  kalshi?: { away: FocusableBook; home: FocusableBook } | null;
  polymarket?: { away: FocusableBook; home: FocusableBook } | null;
}

export type FocusMatch =
  | {
      kind: 'match';
      gameKey: string;
      teamSide: 'away' | 'home';
      venue: FocusedVenue;
    }
  | { kind: 'miss' }
  | { kind: 'ambiguous' }
  | { kind: 'unavailable' };

// ── Builder ───────────────────────────────────────────────────────────

/** Build the focused-dashboard URL for a market target or an explicit
 *  unavailable link. Field order in the emitted query string is fixed
 *  (focus, sport, date, event, ticker, venue, side[, reason]) so the output
 *  is byte-stable for a given input. */
export function focusedDashboardHref(input: FocusedDashboardLinkInput): string {
  const params = new URLSearchParams();
  if (input.kind === 'market') {
    const { target } = input;
    params.set('focus', 'market');
    params.set('sport', target.sport);
    params.set('date', target.date);
    params.set('event', target.eventIdentity);
    params.set('ticker', target.ticker);
    if (target.venue) params.set('venue', target.venue);
    params.set('side', target.side);
  } else {
    params.set('focus', 'unavailable');
    if (input.sport) params.set('sport', input.sport);
    if (input.date) params.set('date', input.date);
    if (input.ticker) params.set('ticker', input.ticker);
    if (input.venue) params.set('venue', input.venue);
    if (input.side) params.set('side', input.side);
    params.set('reason', input.reason);
  }
  return `/dashboard?${params.toString()}`;
}

// ── Parser ────────────────────────────────────────────────────────────

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isRealIsoDate(value: string): boolean {
  if (!ISO_DATE_RE.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

function isFocusedSport(value: string): value is FocusedSport {
  return value === 'atp' || value === 'atp-challenger' || value === 'wta' || value === 'cfb';
}

function isFocusedVenue(value: string): value is FocusedVenue {
  return value === 'kalshi' || value === 'polymarket';
}

function isFocusedSide(value: string): value is FocusedSide {
  return value === 'yes' || value === 'no';
}

function isFocusUnavailableReason(value: string): value is FocusUnavailableReason {
  return value === 'missing-event-identity' || value === 'invalid-source-identity' || value === 'ambiguous-source-identity';
}

/** One query value, or `null` when the key is repeated (a contradiction),
 *  or `undefined` when the key is absent. */
function getOne(params: URLSearchParams, key: string): string | null | undefined {
  const values = params.getAll(key);
  if (values.length === 0) return undefined;
  if (values.length > 1) return null;
  return values[0];
}

/** Parse a focused-dashboard query. Unknown keys never invalidate the link;
 *  a malformed or contradictory `focus=market`/`focus=unavailable` query
 *  always becomes `invalid` — it never falls back to normal auto-selection.
 *  `identity` is a plain string derived from the complete query so callers
 *  can key one-shot hydration state without depending on object identity. */
export function parseFocusedDashboardQuery(params: URLSearchParams): ParsedDashboardFocus {
  const focusValues = params.getAll('focus');
  if (focusValues.length === 0) return { kind: 'none' };

  const identity = params.toString();
  if (focusValues.length > 1) return { kind: 'invalid', identity };

  const focus = focusValues[0];
  if (focus !== 'market' && focus !== 'unavailable') {
    return { kind: 'invalid', identity };
  }

  if (focus === 'unavailable') {
    const sport = getOne(params, 'sport');
    const date = getOne(params, 'date');
    const ticker = getOne(params, 'ticker');
    const venue = getOne(params, 'venue');
    const side = getOne(params, 'side');
    const reason = getOne(params, 'reason');
    if (sport === null || date === null || ticker === null || venue === null || side === null || reason === null) {
      return { kind: 'invalid', identity };
    }
    if (!reason || !isFocusUnavailableReason(reason)) return { kind: 'invalid', identity };
    if (sport !== undefined && !isFocusedSport(sport)) return { kind: 'invalid', identity };
    if (date !== undefined && !isRealIsoDate(date)) return { kind: 'invalid', identity };
    if (venue !== undefined && !isFocusedVenue(venue)) return { kind: 'invalid', identity };
    if (side !== undefined && !isFocusedSide(side)) return { kind: 'invalid', identity };
    return { kind: 'unavailable', identity };
  }

  // focus === 'market'
  const sport = getOne(params, 'sport');
  const date = getOne(params, 'date');
  const event = getOne(params, 'event');
  const ticker = getOne(params, 'ticker');
  const venue = getOne(params, 'venue');
  const side = getOne(params, 'side');
  if (sport === null || date === null || event === null || ticker === null || venue === null || side === null) {
    return { kind: 'invalid', identity };
  }
  if (!sport || !isFocusedSport(sport)) return { kind: 'invalid', identity };
  if (!date || !isRealIsoDate(date)) return { kind: 'invalid', identity };
  if (!event) return { kind: 'invalid', identity };
  if (!ticker) return { kind: 'invalid', identity };
  if (venue !== undefined && !isFocusedVenue(venue)) return { kind: 'invalid', identity };
  if (!side || !isFocusedSide(side)) return { kind: 'invalid', identity };

  const target: FocusedDashboardTarget = {
    sport,
    date,
    eventIdentity: event,
    ticker,
    side,
    ...(venue ? { venue } : {}),
  };
  return { kind: 'market', target, identity };
}

// ── Matcher ───────────────────────────────────────────────────────────

/** True when `row` is the exact event `eventIdentity` names. `eventIdentity`
 *  is either the exact five-part doubleheader-safe row key (compared to
 *  `row.key` verbatim) or the exact two-code away/home pair used by the
 *  existing `/game/...` route (compared in order, unnormalized).
 *
 *  Exported for the Spread/Total board (#539), which resolves line targets
 *  against `GameEventRow`s rather than the moneyline rows this module matches.
 *  Both boards must apply ONE identity rule: a second copy is how the two
 *  paths would drift into disagreeing about which event a link names. */
export function focusEventIdentityMatches(
  eventIdentity: string,
  row: { key: string; awayCode: string; homeCode: string },
): boolean {
  return eventIdentityMatchesRow(eventIdentity, row);
}

function eventIdentityMatchesRow(
  eventIdentity: string,
  row: Pick<FocusableGameRow, 'key' | 'awayCode' | 'homeCode'>,
): boolean {
  const parts = eventIdentity.split('|');
  if (parts.length === 5) return row.key === eventIdentity;
  if (parts.length === 2) {
    const [away, home] = parts;
    return row.awayCode === away && row.homeCode === home;
  }
  return false;
}

interface RowMatch {
  gameKey: string;
  teamSide: 'away' | 'home';
  venue: FocusedVenue;
}

/** Resolve one row/venue's exact book match. Returns `'unavailable'` for a
 *  Polymarket ticket whose ticker matches at least one slot but the caller
 *  supplied no native-YES token to disambiguate — this reuses #92's
 *  fail-closed orientation: a missing or ambiguous native-YES identity opens
 *  nothing, rather than guessing. Returns an empty array for a plain no-op
 *  (the ticker simply isn't on this row/venue at all). */
function matchesForRowAndVenue(
  row: FocusableGameRow,
  venue: FocusedVenue,
  ticker: string,
  polymarketNativeYesTokenId: string | undefined,
): RowMatch[] | 'unavailable' {
  if (venue === 'kalshi') {
    const matches: RowMatch[] = [];
    if (row.kalshi?.away.marketTicker === ticker) matches.push({ gameKey: row.key, teamSide: 'away', venue });
    if (row.kalshi?.home.marketTicker === ticker) matches.push({ gameKey: row.key, teamSide: 'home', venue });
    return matches;
  }

  const slots: Array<{ teamSide: 'away' | 'home'; book: FocusableBook }> = [];
  if (row.polymarket?.away.marketTicker === ticker) slots.push({ teamSide: 'away', book: row.polymarket.away });
  if (row.polymarket?.home.marketTicker === ticker) slots.push({ teamSide: 'home', book: row.polymarket.home });
  if (slots.length === 0) return [];
  if (!polymarketNativeYesTokenId) return 'unavailable';

  return slots
    .filter((slot) => slot.book.tokenId === polymarketNativeYesTokenId)
    .map((slot) => ({ gameKey: row.key, teamSide: slot.teamSide, venue }));
}

/**
 * Resolve a focused-dashboard target against the already-filtered live
 * moneyline `GameRow[]`. Requires exact sport, date, and event identity on a
 * live (non-final, non-archived) row, then an exact ticker match within the
 * requested venue (or, if venue is absent, exactly one venue with an exact
 * match). Polymarket disambiguates a shared ticker by the market's native
 * YES token (`context.polymarketNativeYesTokenId`), never by side — `side`
 * only selects YES/NO within the already-identified market and plays no
 * part in matching.
 *
 * Zero matches is a `miss` (the row may simply not have arrived yet — the
 * caller's hydration hook keeps waiting up to `FOCUSED_DASHBOARD_WAIT_MS`).
 * More than one is `ambiguous`. A missing native-YES identity needed to
 * disambiguate a Polymarket ticker is `unavailable` — that never resolves
 * itself by waiting, so it fails closed immediately.
 */
export function matchFocusedDashboardTarget(
  target: FocusedDashboardTarget,
  rows: readonly FocusableGameRow[],
  context: { polymarketNativeYesTokenId?: string } = {},
): FocusMatch {
  const candidateRows = rows.filter(
    (row) =>
      // #604 — circuit-aware: 'atp' excludes exact-Challenger rows and
      // 'atp-challenger' includes only them; every other sport is equality.
      dashboardSportMatches(row, target.sport) &&
      row.date === target.date &&
      !row.archived &&
      row.gameState !== 'final' &&
      eventIdentityMatchesRow(target.eventIdentity, row),
  );

  const venuesToSearch: FocusedVenue[] = target.venue ? [target.venue] : ['kalshi', 'polymarket'];

  const matches: RowMatch[] = [];
  let unavailable = false;

  for (const row of candidateRows) {
    for (const venue of venuesToSearch) {
      const result = matchesForRowAndVenue(row, venue, target.ticker, context.polymarketNativeYesTokenId);
      if (result === 'unavailable') {
        unavailable = true;
        continue;
      }
      matches.push(...result);
    }
  }

  if (unavailable) return { kind: 'unavailable' };
  if (matches.length === 0) return { kind: 'miss' };
  if (matches.length > 1) return { kind: 'ambiguous' };

  const only = matches[0];
  return { kind: 'match', gameKey: only.gameKey, teamSide: only.teamSide, venue: only.venue };
}
